import { useGetFolders } from '@utils/api/folders/use-get-folders';
import { formatDate, formatDuration } from '@utils/date';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import PageHeader from '@components/layout/page-header';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { capitalizeFirstLetter } from '@utils/capitalize';

type Row = {
  id: string | number;
  filename: string;
  language: string;
  date_upload: string;
  last_modified: string;
  duration: string;
  status: string;
  type: string;
};

export default function SpeechToTextSessions() {
  const { data: foldersData, isLoading, isFetching } = useGetFolders();
  const { t } = useTranslation();

  const rows = useMemo<Row[]>(() => {
    if (!foldersData?.result?.documents) return [];
    return foldersData.result.documents.map(transcript => ({
      id: transcript.id,
      filename: transcript.filename,
      language: capitalizeFirstLetter(
        transcript.language || transcript.session?.language_key || ''
      ),
      date_upload: transcript.session?.date_upload
        ? formatDate(transcript.session.date_upload)
        : '',
      type: capitalizeFirstLetter(transcript.type),
      duration: formatDuration(transcript.session?.duration),
      last_modified: formatDate(transcript.last_modified),
      status:
        transcript.session?.status === 'finished' ? t('completed') : t('error')
    }));
  }, [foldersData, t]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        id: 'id',
        header: t('session-id'),
        field: 'id',
        minWidth: 260,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'filename',
        header: t('filename'),
        field: 'filename',
        minWidth: 280,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },

      // --- Date range filtering for upload date (server fields: date_from / date_to) ---
      // "From" column – filter-only (no cell content)
      {
        id: 'uploaded_from',
        header: t('upload-date') + ' • ' + t('from'),
        field: 'date_upload', // display field (unused for cell)
        filterKey: 'date_from', // API field
        minWidth: 160,
        maxWidth: 200,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'date',
        // render nothing in the body; just offers the header filter
        cell: () => null
      },
      // "To" column – filter-only (no cell content)
      {
        id: 'uploaded_to',
        header: t('upload-date') + ' • ' + t('to'),
        field: 'date_upload',
        filterKey: 'date_to', // API field
        minWidth: 160,
        maxWidth: 200,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'date',
        cell: () => null
      },

      // Visible upload date column (read-only display)
      {
        id: 'date_upload',
        header: t('upload-date'),
        field: 'date_upload',
        minWidth: 160,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },

      {
        id: 'last_modified',
        header: t('last-modified'),
        field: 'last_modified',
        minWidth: 160,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'duration',
        header: t('duration'),
        field: 'duration',
        minWidth: 120,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'status',
        header: t('status'),
        field: 'status',
        minWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('completed'), value: t('completed') },
          { label: t('error'), value: t('error') }
        ],
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'language',
        header: t('language'),
        field: 'language',
        minWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'type',
        header: t('type'),
        field: 'type',
        minWidth: 120,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ];
  }, [t]);

  const total = foldersData?.total_results ?? foldersData?.count ?? rows.length;

  return (
    <div className="bg-app min-h-screen">
      <PageHeader
        title={t('sessions')}
        caption={t(
          'a-list-of-all-the-files-in-your-account-including-their-name-duration-language-and-status'
        )}
      />

      <div className="px-4 sm:px-0 py-4 sm:pb-0">
        {isLoading ? (
          <Skeleton variant="table" rows={8} />
        ) : (
          <DataGridV2<Row>
            data={rows}
            columns={columns}
            enableRowSelection={false}
            enablePagination
            manualPagination
            totalItems={total}
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            showColumnFilters={false}
            filterMode="popover"
            height="65vh"
            dense
            striped
            grid={false}
            loading={isLoading || isFetching || !foldersData}
            getRowId={row => String(row.id)}
          />
        )}
      </div>
    </div>
  );
}
